/**
 * Add from the brand library (stage 2b): pick pictures from the brand's media
 * library and copy them into the project, through the same call the room has
 * always used (POST /workshop/projects/:id/media, addFromLibrary). The project
 * keeps its own copy. A picture already in the project says so and cannot be
 * picked again (the API refuses a second copy).
 *
 * The library has no search route, so Find filters the page on screen and
 * says so.
 */
import { useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, ImageIcon, Search } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { Image } from "@/components/Image";
import type { BrandMediaItem } from "@/types/brandMedia.type";
import { useBrandMedia } from "@/app/routes/dashboard/api/useBrandMedia";
import {
  newMediaPreviewId,
  useWorkshopMedia,
  useWorkshopMediaMutations,
} from "../api/useWorkshopMedia";
import WorkshopDialog from "./WorkshopDialog";
import { WsInlineError } from "./WorkshopParts";

const PAGE_SIZE = 12;

type Props = {
  brandId: string;
  projectId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function WorkshopLibraryPicker({ brandId, projectId, isOpen, onOpenChange }: Props) {
  const [page, setPage] = useState(1);
  const [find, setFind] = useState("");
  const [picked, setPicked] = useState<Map<string, BrandMediaItem>>(() => new Map());
  const library = useBrandMedia(isOpen ? brandId : null, { page, limit: PAGE_SIZE });
  const projectMedia = useWorkshopMedia(isOpen ? projectId : null);
  const { addFromLibrary } = useWorkshopMediaMutations(projectId);

  // Library pictures this project already has a copy of.
  const inProject = useMemo(() => {
    const ids = new Set<string>();
    for (const m of projectMedia.data ?? []) {
      if (m.copiedFromBrandMediaId) ids.add(m.copiedFromBrandMediaId);
      if (m.library?.mediaId) ids.add(m.library.mediaId);
    }
    return ids;
  }, [projectMedia.data]);

  const items = library.data?.media ?? [];
  const total = library.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const q = find.trim().toLowerCase();
  const shown = q ? items.filter((m) => m.fileName.toLowerCase().includes(q)) : items;

  const close = () => {
    setPicked(new Map());
    setFind("");
    setPage(1);
    onOpenChange(false);
  };

  const toggle = (item: BrandMediaItem) =>
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });

  const add = () => {
    // Each copy shows in the room at once and leaves again if it is refused.
    for (const item of picked.values()) {
      addFromLibrary.mutate({ item, previewId: newMediaPreviewId() });
    }
    close();
  };

  const count = picked.size;

  return (
    <WorkshopDialog
      isOpen={isOpen}
      onOpenChange={(open) => (open ? onOpenChange(true) : close())}
      title="Add from the brand library"
      description="Pick from the brand's library. The project keeps its own copy."
      className="ws-libpick"
      footnote={count > 0 ? `${count} picked` : undefined}
      cancelLabel="Cancel"
      confirmLabel={count > 1 ? `Add ${count} pictures` : count === 1 ? "Add 1 picture" : "Add pictures"}
      confirmDisabled={count === 0}
      onConfirm={add}
    >
      <label className="ws-share-search ws-picker-find">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          type="search"
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder={pages > 1 ? "Find on this page" : "Find by name"}
          aria-label={pages > 1 ? "Find a picture on this page" : "Find a picture by name"}
        />
      </label>

      {library.isError && !library.data ? (
        <WsInlineError
          className="ws-inline-error--compact"
          title="Couldn't load the brand library"
          onRetry={() => void library.refetch()}
        />
      ) : library.isLoading ? (
        <div className="ws-picker-grid" aria-busy="true" aria-label="Loading the brand library">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="ws-picker-tile ws-picker-tile--skel ws-skel" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="ws-share-note">
          <ImageIcon className="me-1.5 inline size-3.5 align-[-2px]" strokeWidth={1.8} aria-hidden="true" />
          The brand library is empty. Pictures copied to it from a project, or uploaded on the Media page, show here.
        </p>
      ) : shown.length === 0 ? (
        <p className="ws-share-note">Nothing on this page matches.</p>
      ) : (
        <div className="ws-picker-grid" role="group" aria-label="Brand library pictures">
          {shown.map((item) => {
            const here = inProject.has(item.id);
            const on = picked.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                className="ws-picker-tile"
                aria-pressed={on}
                disabled={here}
                onClick={() => toggle(item)}
                aria-label={here ? `${item.fileName}, already in this project` : item.fileName}
              >
                <span className="ws-picker-frame">
                  {item.thumbUrl || item.url ? (
                    <Image src={item.thumbUrl ?? item.url ?? undefined} alt="" className="ws-picker-img" />
                  ) : (
                    <ImageIcon className="ws-picker-none size-5" strokeWidth={1.6} aria-hidden="true" />
                  )}
                  {on ? (
                    <span className="ws-picker-check" aria-hidden="true">
                      <Check className="size-3.5" strokeWidth={2.4} />
                    </span>
                  ) : null}
                  {here ? <span className="ws-tag ws-picker-here">In this project</span> : null}
                </span>
                <span className="ws-picker-name">{item.fileName}</span>
              </button>
            );
          })}
        </div>
      )}

      {pages > 1 ? (
        <div className="ws-picker-pager">
          <KitButton
            variant="secondary"
            iconLeft={ChevronLeft}
            className="ws-dialog-btn"
            isDisabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          />
          <span className="ws-meta">
            Page {page} of {pages}
          </span>
          <KitButton
            variant="secondary"
            iconLeft={ChevronRight}
            className="ws-dialog-btn"
            isDisabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Next page"
          />
        </div>
      ) : null}
    </WorkshopDialog>
  );
}
